import * as Yup from 'yup';
import { TType } from '../hooks/useTranslation';
import { ESTABLISHMENT_TYPE } from '../components/ProjectTypeSelect/utils';
import { TransactionType } from '../models/project';

const commonFields = (t: TType) => ({
  unitCategory: Yup.string().required('Unit Category is required').nullable(),
  projectName: Yup.string()
    .required(t('validationMessages.projectNameIsRequired'))
    .nullable(),
  unitType: Yup.string()
    .required(t('validationMessages.unitTypeIsRequired'))
    .nullable(),
  transactionType: Yup.string()
    .required(t('validation.transactionTypeRequired'))
    .nullable(),
  isLeasedFromAnotherIngkaCompany: Yup.string().when('transactionType', {
    is: TransactionType.LEASE,
    then: Yup.string().required(t('validation.leasedFromIngkaRequired')),
    otherwise: Yup.string().nullable().notRequired(),
  }),
  countryName: Yup.string().required(t('validation.country')).nullable(),
  siteType: Yup.string().required(t('validation.siteType')).nullable(),
  confidential: Yup.bool().required(t('validation.confidential')).nullable(),
});

const investmentCategorySchema = (t: TType) =>
  Yup.object().shape({
    propertyInvestment: Yup.number()
      .nullable()
      .min(
        Yup.ref('landCost'),
        t('validationMessages.propertyInvestmentShouldBeMoreThanLandCost'),
      ),
    landCost: Yup.number().nullable(),
  });

const stockedFields = (t: TType) => ({
  stockedRangeAdjustment: Yup.string()
    .required(t('validation.inboundStockedRange'))
    .nullable(),
  storageCapacityAdjustment: Yup.string()
    .required(t('validation.inboundStorageCapacity'))
    .nullable(),
  outboundTruckCapacity: Yup.string()
    .required(t('validation.outboundTruckCapacity'))
    .nullable(),
  outboundParcelCapacity: Yup.string()
    .required(t('validation.outboundParcelCapacity'))
    .nullable(),
});

const projectDateFields = (t: TType) => ({
  projectStartDate: Yup.string()
    .required(t('validation.projectStartDate'))
    .nullable(),
  projectCompletionDate: Yup.string()
    .required(t('validation.projectOpeningDate'))
    .nullable(),
  projectClosingDate: Yup.string()
    .required(t('validation.projectClosingDate'))
    .nullable(),
});
export const investmentFields = (t: TType, isep?: boolean) => ({
  investments: Yup.array().of(
    Yup.object().shape({
      BUDGET: isep
        ? Yup.mixed().notRequired()
        : investmentCategorySchema(t).nullable(),
      FORECAST: Yup.mixed().when('isep', {
        is: true,
        then: investmentCategorySchema(t),
        otherwise: Yup.mixed().notRequired(),
      }),

      RELEASED_APPROVED_BUDGET: Yup.mixed().when('isep', {
        is: true,
        then: investmentCategorySchema(t),
        otherwise: Yup.mixed().notRequired(),
      }),
    }),
  ),
});

const returnNewUnitSchema = (t: TType) =>
  Yup.object().shape({
    ...commonFields(t),
    ...stockedFields(t),
  });

const returnCloseSchema = (t: TType) =>
  Yup.object().shape({
    ...commonFields(t),
    ...projectDateFields(t),
  });

const returnRelocationSchema = (t: TType) =>
  Yup.object().shape({
    ...commonFields(t),
    ...projectDateFields(t),
    ...stockedFields(t),
  });

export const validationSchema = (t: TType, projectType: ESTABLISHMENT_TYPE) => {
  switch (projectType) {
    case ESTABLISHMENT_TYPE.NEW_UNIT:
      return returnNewUnitSchema(t);
    case ESTABLISHMENT_TYPE.CLOSE:
      return returnCloseSchema(t);
    case ESTABLISHMENT_TYPE.IMPROVE:
    case ESTABLISHMENT_TYPE.RELOCATION:
    case ESTABLISHMENT_TYPE.FORMAT_CHANGE:
      return returnRelocationSchema(t);
  }
};
export const updateUserValidation = (t: TType) => ({
  name: Yup.string().required(t('validationMessages.nameIsRequired')),
  email: Yup.string()
    .email(t('validationMessages.mustBeAValidEmail'))
    .max(255)
    .required(t('validationMessages.emailIsRequired')),
  expansionUserRole: Yup.string().required(
    t('validationMessages.userMustHaveARole'),
  ),
  countries: Yup.array()
    .min(1)
    .required(t('validationMessages.userMustHaveAtLeastOneCountry')),
});

export const userValidation = (t: TType) =>
  Yup.object().shape(updateUserValidation(t));

export const positiveOnChange = (e, onChange) => {
  const regex = /^\d+$/;
  if (regex.test(e.target.value)) {
    onChange(e);
  }
  if (e.target.value === '') {
    onChange(e);
  }
};
